import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { zustandStorageMiddleware } from '../infrastructure/storage/zustandStorageMiddleware';
import type { AppNotification } from '@/types/appNotification';

// ── tmp types — auto-read + never persisted ───────────────────────────────────
export const TMP_NOTIFICATION_TYPES = new Set([
    "new_order",
    "update_order",
    "order_delivered",

    // add more tmp types here
]);

const isTmp = (type: string | undefined): boolean =>
    TMP_NOTIFICATION_TYPES.has(type ?? "");

// ── constants ─────────────────────────────────────────────────────────────────
const MAX_NOTIFICATIONS = 5000;
const MAX_PERSISTED = 500;

// ── types ─────────────────────────────────────────────────────────────────────
type NotificationState = {
    notifications: AppNotification[];
    unread_count: number;
    dirty: boolean;
    cleared_at: number;

    addNotification: (notification: AppNotification) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    removeNotification: (id: string) => void;
    clearNotifications: () => void;
};

// ── helpers ───────────────────────────────────────────────────────────────────
const countUnread = (notifications: AppNotification[]): number =>
    notifications.filter(n => !n.is_read).length;

// ── store ─────────────────────────────────────────────────────────────────────
export const useNotificationStore = create<NotificationState>()(
    persist(
        (set, get) => ({

            notifications: [],
            unread_count: 0,
            dirty: false,
            cleared_at: 0,

            addNotification: (notification) => {
                // skip if older than last clear
                if (notification.created_at <= get().cleared_at) return;

                // skip duplicates
                if (get().notifications.some(n => n.id === notification.id)) return;

                const notifications = [
                    {
                        ...notification,
                        // tmp types are auto-marked as read
                        is_read: isTmp(notification.type) ? true : notification.is_read,
                    },
                    ...get().notifications,
                ].slice(0, MAX_NOTIFICATIONS);

                const unread_count = countUnread(notifications);

                set({ notifications, unread_count, dirty: unread_count > 0 });
            },

            markAsRead: (id) => {
                const notifications = get().notifications.map(n =>
                    n.id === id ? { ...n, is_read: true } : n
                );
                const unread_count = countUnread(notifications);
                set({ notifications, unread_count, dirty: unread_count > 0 });
            },

            markAllAsRead: () => {
                const notifications = get().notifications.map(n =>
                    ({ ...n, is_read: true })
                );
                set({ notifications, unread_count: 0, dirty: false });
            },

            removeNotification: (id) => {
                const notifications = get().notifications.filter(n => n.id !== id);
                const unread_count = countUnread(notifications);
                set({ notifications, unread_count, dirty: unread_count > 0 });
            },

            clearNotifications: () =>
                set({
                    notifications: [],
                    unread_count: 0,
                    dirty: false,
                    cleared_at: Date.now(),
                }),
        }),

        {
            name: 'notification-storage',
            storage: zustandStorageMiddleware,
            partialize: (state) => ({
                // tmp types are never persisted
                notifications: state.notifications
                    .filter(n => !isTmp(n.type))
                    .slice(0, MAX_PERSISTED),
                unread_count: state.unread_count,
                dirty: state.dirty,
                cleared_at: state.cleared_at,
            }),
        }
    )
);